/** Wire protocol v2 shared with engine/interligne/app.py. */

export type TextPair = { stable: string; pending: string };

export type Segment = {
  id: string;
  index: number;
  startS: number;
  endS: number;
  lang: string;
  targetLang: string;
  source: TextPair;
  target: TextPair;
  final: boolean;
  targetFinal: boolean;
  incomplete: boolean;
  /** Finished translations into listeners' languages, by language code. */
  translations?: Record<string, string>;
  /** Soniox sessions: "main", or the language of a listener-only stream. */
  track?: string;
};

export type SessionState = "listening" | "paused" | "finalizing" | "stopped" | "error";
export type Direction = "one-way" | "two-way";
export type Plan = { direction: Direction; source: string; target: string };

export type Metrics = {
  audioS?: number;
  lagS?: number;
  queued?: number;
  revisions?: number;
  failures?: number;
  partials?: number;
  finals?: number;
  refines?: number;
  dropped?: number;
  gaps?: number;
  firstTextP50Ms?: number | null;
  finalP50Ms?: number | null;
  finalP95Ms?: number | null;
};

export type ReadyEvent = {
  type: "ready";
  protocol: number;
  role: "speaker" | "listener";
  sessionId: string;
  code: string;
  resumed: boolean;
  mode: "live" | "simulated";
  simulated: boolean;
  provider: string;
  plan: Plan;
  state: SessionState;
  resumeToken?: string;
  limits?: { maxSessionS: number; resumeGraceS: number };
  /** Listener only: languages they may choose, voices available (code -> sample rate). */
  listenerLanguages?: string[];
  voices?: Record<string, number>;
  lang?: string | null;
  audio?: boolean;
  track?: string;
  /** The session's attendee languages: the target, plus an optional second one. */
  attendeeLanguages?: string[];
  /** Seconds listeners' phones buffer translated speech before playing it. */
  audioDelayS?: number;
  /** Real-time engine (Soniox): play speech as it arrives, no buffer, no pacing. */
  realtime?: boolean;
};

export type ServerEvent =
  | ReadyEvent
  | { type: "snapshot"; seq: number; state: SessionState; segments: Segment[] }
  | { type: "segment"; seq: number; segment: Segment }
  | { type: "remove"; seq: number; id: string; reason?: string }
  | { type: "status"; seq?: number; state: SessionState }
  | { type: "error"; seq?: number; code: string; message: string; fatal: boolean }
  | ({ type: "metrics" } & Metrics)
  | { type: "pong"; t?: number }
  | { type: "prefs"; lang: string | null; audio: boolean; track?: string }
  | { type: "audio_start"; segmentId: string; lang: string; sampleRate: number; speechStartS?: number; speechNowS?: number }
  | { type: "audio_end"; segmentId: string; lang: string }
  | { type: "settings"; audioDelayS: number };

const KNOWN = new Set([
  "ready",
  "snapshot",
  "segment",
  "remove",
  "status",
  "error",
  "metrics",
  "pong",
  "prefs",
  "audio_start",
  "audio_end",
  "settings",
]);

/** Parse one text frame. Malformed or unknown frames return null instead of throwing. */
export function parseEvent(raw: string): ServerEvent | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== "string" || !KNOWN.has(type)) return null;
  if (type === "segment" && !isSegment((value as { segment?: unknown }).segment)) return null;
  if (type === "snapshot" && !Array.isArray((value as { segments?: unknown }).segments)) return null;
  return value as ServerEvent;
}

function isSegment(value: unknown): value is Segment {
  if (!value || typeof value !== "object") return false;
  const s = value as Partial<Segment>;
  return (
    typeof s.id === "string" &&
    typeof s.index === "number" &&
    typeof s.source?.stable === "string" &&
    typeof s.target?.stable === "string"
  );
}

export function seqOf(event: ServerEvent): number | null {
  return "seq" in event && typeof event.seq === "number" ? event.seq : null;
}

export function joinText(pair: TextPair): string {
  return [pair.stable, pair.pending].filter(Boolean).join(" ");
}
